import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, X, ChevronLeft, ChevronRight, Volume2, VolumeX } from "lucide-react";
import axios from "axios";
import Logo from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ────────────────────────────────────────────────────────────────
   MY MOOD GROOVE — INVESTOR PITCH DECK
   11 cinematic slides. Keyboard nav, progress dots, slide counter.
   ──────────────────────────────────────────────────────────────── */

const IMG = {
  // Original 5
  cosmic:   "/pitch/slide-cosmic-connection.png",  // fragmented signals + city
  mountain: "/pitch/slide-mountain-path.png",      // path over mountains
  orb:      "/pitch/slide-meditation-orb.png",     // hands holding light orb
  swirls:   "/pitch/slide-seated-swirls.png",      // seated figure with dot swirls
  portal:   "/pitch/slide-standing-portal.png",    // standing figure at cosmic portal
  // New 5
  ambient:  "/pitch/slide-ambient-room.png",       // NOTE: file actually contains the ocean sunset + water reflection (misnamed)
  hologram: "/pitch/slide-hologram-table.png",     // hands over 3D hologram data
  coastal:  "/pitch/slide-coastal-connection.png", // two figures across coastline
  heart:    "/pitch/slide-heart-wearable.png",     // hand on heart + wrist wearable
  ocean:    "/pitch/slide-ocean-wedge.png",        // NOTE: file actually contains the bean-bag room with waves on wall (misnamed)
};

const SLIDES = [
  {
    n: "01",
    eyebrow: "My Mood Groove",
    title: "A private AI awareness platform that turns mood, conversation, sound, and reflection into a ritual of personal evolution.",
    kicker: "Sound. Visuals. Scent. And beyond.",
    footer: "From moment to immersion.",
    image: IMG.portal,
    focal: "right",
    align: "left",
    overlay: "stars",
    variant: "cover",
  },
  {
    n: "02",
    eyebrow: "The Problem",
    title: "People have more wellness data, but less emotional clarity.",
    body: "Most tools measure the body, deliver content, or answer questions. Few help a person understand what is happening inside them — and learn from it over time.",
    bullets: [
      { label: "Signals without meaning", text: "Wearables surface stress, sleep, and recovery data without explaining the lived emotional context." },
      { label: "Content without continuity", text: "Meditation and wellness libraries deliver sessions, but rarely build a private memory of what helps this individual." },
      { label: "Conversation without atmosphere", text: "General AI can respond, but it does not unite voice, soundscape, reflection, and ritual into a coherent experience." },
    ],
    pull: "The unmet need is not more content. It is a trusted ritual that connects feeling, context, response, and learning.",
    image: IMG.cosmic,
    focal: "right",
    align: "left",
  },
  {
    n: "03",
    eyebrow: "The Solution",
    title: "My Mood Groove turns a moment of feeling into a personalized awareness ritual.",
    body: "The user chooses a mood and a guide, enters a voice or text conversation, receives an adaptive soundscape, and leaves with an Awareness Signal and a meaningful next step. A wearable bridge — Apple Watch, Oura, Fitbit, and more, integrated through Open Wearables — lets body signals quietly cross-reference the conversation.",
    steps: [
      { n: "1", label: "Choose",    text: "Name the atmosphere" },
      { n: "2", label: "Converse",  text: "Speak or type privately" },
      { n: "3", label: "Regulate",  text: "Pair guidance with sound" },
      { n: "4", label: "Recognize", text: "Decode the moment" },
      { n: "5", label: "Return",    text: "Build continuity over time" },
    ],
    image: IMG.orb,
    focal: "right",
    align: "left",
    overlay: "orb",
  },
  {
    n: "04",
    eyebrow: "The Product",
    title: "A simple first experience opens into a learning architecture.",
    body: "The AI is not the destination. The user is.",
    steps: [
      { n: "1", label: "Session input",         text: "Mood, conversation, wearable signals, context" },
      { n: "2", label: "Guide & sound",         text: "The right mode, voice, and atmosphere" },
      { n: "3", label: "Awareness Signal",      text: "Recognition words, reflection, tiny action" },
      { n: "4", label: "Memory continuity",     text: "Patterns linked across time" },
      { n: "5", label: "Next ritual",           text: "A personalized reason to return" },
    ],
    pull: "Pick a mood. Choose a guide. Enter your groove.  ·  Awareness in the moment. Evolution over time.",
    image: IMG.hologram,
    focal: "right",
    align: "left",
  },
  {
    n: "05",
    eyebrow: "Mood Insights",
    title: "What accumulates over time.",
    body: "One session becomes ten. Ten becomes a rhythm. Each response feeds a private biometric longitudinal library — biological and psychological signals recognized together over time: stress, rest, focus, location, and signals bridged from the wearables they already own, always with consent.",
    items: [
      { tag: "Recognize", text: "The dashboard fine-tunes emotional recognition of what a person has been going through — not a diagnosis, a pattern." },
      { tag: "Return",    text: "The grooves that genuinely worked resurface, so one tap returns to the meditations that helped most." },
      { tag: "Share",     text: "A groove can be passed to someone who needs it — the atmosphere travels, the conversation never does." },
    ],
    metrics: [
      { label: "Vibe",   value: 88, color: "#7de3c5", icon: "sparkles" },
      { label: "Energy", value: 84, color: "#EED89A", icon: "battery" },
      { label: "Focus",  value: 82, color: "#6ea3ff", icon: "focus" },
      { label: "Rest",   value: 90, color: "#c5b0ff", icon: "moon" },
    ],
    trend: {
      labels: ["DEC", "JAN", "FEB", "MAR", "APR", "MAY"],
      vibe:   [40, 50, 58, 66, 78, 88],
      energy: [48, 56, 62, 70, 78, 84],
      focus:  [42, 50, 60, 65, 74, 82],
      rest:   [45, 55, 65, 72, 80, 90],
    },
    pull: "The moment the user starts trusting the pattern is the moment they stop looking for another app.",
    image: null,
    focal: "right",
    align: "left",
    variant: "dashboard",
    scroll: true,
  },
  {
    n: "06",
    eyebrow: "Proof of Progress",
    title: "The concept has moved from idea to an integrated product system.",
    body: "Current evidence is product and partner readiness. User traction will be the next proof layer.",
    pillars: [
      { tag: "Build",     head: "Working prototype",       text: "FlutterFlow, Firebase, OpenAI conversation flow, mood and guide selection, and soundscape playback." },
      { tag: "Retention", head: "Defined retention loop",  text: "Awareness Signal, saved grooves, pattern recognition, and personalized return rituals." },
      { tag: "IP",        head: "Protected architecture",  text: "A second provisional filing documents multisensory atmosphere, wearable integration, sharing, vehicles, and embodied systems." },
      { tag: "Pilot",     head: "Institutional pathway",   text: "A nurse wellbeing pilot concept and partner portal create a credible first validation environment." },
    ],
    image: IMG.swirls,
    focal: "left",
    align: "right",
    overlay: "path",
    softVignette: true,
  },
  {
    n: "07",
    eyebrow: "The Validation Plan",
    title: "The next milestone is measurable proof that the ritual changes state and earns return visits.",
    body: "The first pilot should test value before scale.",
    steps: [
      { n: "1", label: "Activation",       text: "Complete the first guided groove" },
      { n: "2", label: "Immediate value",  text: "Self-reported before and after shift" },
      { n: "3", label: "Ritual return",    text: "7-day and 30-day repeat use" },
      { n: "4", label: "Continuity",       text: "Saved grooves and recognized patterns" },
      { n: "5", label: "Body–mind bridge", text: "Optional HR and HRV change" },
    ],
    pull: "Initial pilot design: 25–30 participants · 6–8 weeks · qualitative interviews · behavior and wellbeing measures. No outcome claims will be made until the pilot data exists.",
    image: IMG.mountain,
    focal: "left",
    align: "right",
  },
  {
    n: "08",
    eyebrow: "The Moat",
    title: "Why this is difficult to repeat.",
    body: "The moat is the orchestration layer, not a single AI feature. My Mood Groove combines emotional context, adaptive atmosphere, continuity, and multisensory expansion within one governed architecture.",
    bullets: [
      { label: "Experience architecture",  text: "Mood, guide, conversation, sound, signal, and ritual return operate as one loop." },
      { label: "Private awareness memory", text: "Learning compounds around the individual while preserving agency and privacy." },
      { label: "Doctrine system",          text: "Behavioral, mood, guide, safety, and sensory doctrines create consistency beyond generic prompting." },
      { label: "Expansion rights",         text: "Provisional coverage extends toward wearables, visual, scent, projection, sharing, vehicles, and robots." },
    ],
    image: IMG.ambient,
    focal: "left",
    align: "right",
    overlay: "ocean",
  },
  {
    n: "09",
    eyebrow: "The Working Hypothesis",
    quote: "Longitudinal integration of multimodal biometric signals with activity, location, environmental context, life domain, cultural context, and repeated first-person emotional interpretation will produce more accurate and personally meaningful models of physiological and emotional state than population-based biometric classification alone.",
    footer: "The scientific foundation for everything we're building.",
    image: null,
    focal: "right",
    align: "left",
    variant: "thesis",
  },
  {
    n: "10",
    eyebrow: "Business Model",
    title: "One intelligence platform. Six revenue layers.",
    body: "Every layer runs on the same governed architecture — mood interpretation, adaptive atmosphere, and private memory. Each audience pays for a different kind of access to it.",
    layers: [
      { n: "01", payer: "The Individual",         what: "pays for personal access",   detail: "Consumer subscriptions · $9.99–$19.99 per month" },
      { n: "02", payer: "The Institution",        what: "pays for population access", detail: "$25–$100 per person, per year" },
      { n: "03", payer: "The Creator",            what: "pays through revenue share", detail: "20–30% platform share of every sale" },
      { n: "04", payer: "The Brand",              what: "pays for the experience",    detail: "Sponsored grooves that support, never interrupt" },
      { n: "05", payer: "The Property",           what: "pays for the atmosphere",    detail: "Hotels, resorts, spas & wellness spaces" },
      { n: "06", payer: "The Technology Partner", what: "pays for the engine",        detail: "API & white-label licensing" },
    ],
    pull: "Not twelve unrelated businesses — one platform monetized through every relationship it creates.",
    image: null,
    focal: "right",
    align: "left",
    variant: "layers",
  },
  {
    n: "11",
    eyebrow: "Revenue Streams",
    title: "Recurring software first. Marketplace scale next. Atmosphere last.",
    body: "The sequence is the strategy — each phase funds and de-risks the next. Hardware comes only after the software relationship is proven.",
    phases: [
      {
        phase: "Phase 1", name: "Prove", focus: "Recurring software", color: "#EED89A",
        items: [
          { head: "Institutional nurse pilot", text: "A pilot with a national nurse organization becomes the proof point for the entire institutional stream" },
          { head: "Consumer subscriptions", text: "Free entry · Premium $9.99/mo · Immersive $19.99/mo with wearable inputs & exclusive worlds" },
          { head: "Workforce programs", text: "Nurses, first responders, military, caregivers, teachers — licensed at $25–$100 per person annually" },
        ],
      },
      {
        phase: "Phase 2", name: "Expand", focus: "Content & marketplace", color: "#a78bfa",
        items: [
          { head: "Groove packs", text: "Deep Sleep · Pre-Shift Focus · Grief & Reflection · Creative Flow — $4.99–$29.99 each" },
          { head: "Creator marketplace", text: "Musicians, guides, clinicians & sound designers publish grooves — 20–30% platform share" },
          { head: "Artist worlds & brand grooves", text: "Branded emotional environments — the 'AI vibe label' — plus trust-first sponsorships" },
        ],
      },
      {
        phase: "Phase 3", name: "Extend", focus: "Physical & platform", color: "#7de3c5",
        items: [
          { head: "Hospitality licensing", text: "Guestrooms, spas & wellness spaces — per-property and per-room recurring fees" },
          { head: "Atmosphere hardware & MMG ring", text: "Dedicated My Mood Groove ring ~$199 · console $249–$699 · scent refills $29–$59/mo · ecosystem membership $49–$99/mo" },
          { head: "White-label, API & research", text: "License the intelligence engine to platforms; consented research partnerships" },
        ],
      },
    ],
    pull: "My Mood Groove begins with recurring institutional and consumer software revenue, expands through specialized content and a creator marketplace, and ultimately becomes an adaptive atmosphere platform connecting AI conversation, sound, scent, projection, and wearable intelligence.",
    image: null,
    focal: "right",
    align: "left",
    variant: "streams",
  },
  {
    n: "12",
    eyebrow: "Market Entry",
    title: "Begin with people whose stress is visible, repeated, and costly.",
    body: "The initial wedge is nurse wellbeing, followed by adjacent high-intensity communities and the broader consumer market.",
    pillars: [
      { tag: "First wedge", head: "Nurses & nurse leaders", text: "A defined population with repeated stress, measurable wellbeing needs, and institutional distribution." },
      { tag: "Adjacent",    head: "Veterans, first responders, caregivers", text: "Communities where private decompression and continuity can create meaningful value." },
      { tag: "Expansion",   head: "Consumer awareness & wellness",          text: "People already using meditation, AI companions, wearables, sleep, or mood tools." },
    ],
    pull: "Bottom-up SOM: obtainable institutions × eligible users × annual contract value. TAM, SAM, and SOM will be finalized from validated pricing and channel assumptions before circulation.",
    image: IMG.ocean,
    focal: "left",
    align: "right",
  },
  {
    n: "13",
    eyebrow: "The Team",
    title: "A founder's integrated vision, a dedicated build team, and clinical governance by design.",
    body: "The build team is in place and shipping. The next hires convert that momentum into a scalable, evidence-led company.",
    pillars: [
      { tag: "Current",     head: "Greg Demirjian · Founder & CEO",              text: "Creative director with experience in film effects, interactive holographic presentations, music, composition, and patented product development." },
      { tag: "Build team",  head: "Matt Murphy & Beth Wilson · Faction Group",   text: "AI-directed product engineering lab dedicated to the app build. Murphy: 30 years, five technology cycles, enterprise software shipped to 55+ countries. Wilson: co-founder & CPO, architect of the AI-directed build methodology." },
      { tag: "Partner",     head: "Open Wearables · Wearable Data Bridge",       text: "Open-source health intelligence platform (MIT-licensed, self-hosted, HIPAA-ready) — one normalized API bridging Apple Watch, Oura, Fitbit, Garmin, Whoop and more into the app, with open sleep and resilience scoring." },
      { tag: "Recruiting",  head: "Founding Clinical Advisor · Behavioral Health & Emotional Safety", text: "A licensed psychotherapist shaping emotionally responsible conversations and Grooves — defines what the AI may and may not say, owns protocols for distress, trauma, and other high-risk disclosures, and routes people toward human support." },
      { tag: "Recruiting",  head: "Technical Officer · AI & Platform Architecture", text: "Owns scalable infrastructure, privacy, model orchestration, wearable integrations, and product engineering." },
      { tag: "Prospective", head: "Philosopher & Ethics Advisor · Human Meaning & Governance", text: "Strengthens the doctrine system, questions of agency, identity, reflection, and responsible long-term awareness." },
    ],
    pull: "The Clinical Advisor reviews every program — sleep, stress, grief, focus, recovery — shapes the nurse pilot's outcome measures, vets all clinical claims, and anchors partnerships with hospitals, associations, and insurers.",
    image: IMG.heart,
    focal: "left",
    align: "right",
    scroll: true,
  },
  {
    n: "14",
    eyebrow: "The Ask",
    title: "Raising $750,000 to reach pilot evidence and a scalable beta.",
    body: "An 18-month pre-seed plan designed to turn the current architecture into validated product traction.",
    allocations: [
      { pct: 40, label: "Product & technical team",   color: "#a78bfa" },
      { pct: 20, label: "AI safety, privacy, & data", color: "#60a5fa" },
      { pct: 15, label: "Pilot design & validation",  color: "#EED89A" },
      { pct: 15, label: "Content, sound, & experience", color: "#f6a26a" },
      { pct: 10, label: "Legal, IP, & operations",    color: "#e46a5a" },
    ],
    milestones: [
      "Scalable beta",
      "First institutional pilot",
      "Measured retention and state shift",
      "Wearable bridge",
      "Seed readiness",
    ],
    pull: "My Mood Groove is building the awareness layer between personal AI, emotional life, and the atmosphere around us.",
    image: null,
    focal: "right",
    align: "left",
    variant: "ask",
  },
];

/* ────────────────────────────────────────────────────────────────
   Slide layout components
   ──────────────────────────────────────────────────────────────── */

/* Per-slide ambient particle fields — one engine, palette tuned to each image's aura */
const SLIDE_FX = {
  "01": { palette: ["#c5b0ff", "#9d8cff", "#EED89A"], count: 26 },  // cosmic portal city — violet
  "02": { palette: ["#7de3c5", "#a78bfa", "#EED89A"], count: 24 },  // fragmented signals — teal/violet
  "03": { palette: ["#EED89A", "#f6c76a", "#fff3d6"], count: 26 },  // light orb — warm gold
  "04": { palette: ["#6ea3ff", "#7de3c5", "#c5b0ff"], count: 24 },  // hologram — electric blue
  "05": { palette: ["#7de3c5", "#EED89A", "#a78bfa"], count: 14 },  // dashboard — editorial
  "06": { palette: ["#a78bfa", "#7de3c5", "#EED89A"], count: 24 },  // dot swirls — violet/teal
  "07": { palette: ["#9db8ff", "#ffffff", "#EED89A"], count: 22 },  // mountain dawn — cool
  "08": { palette: ["#EED89A", "#f6c76a", "#8fb8e3"], count: 24 },  // ocean sunset — gold/sea
  "09": { palette: ["#EED89A", "#a78bfa"], count: 12 },             // thesis — editorial
  "10": { palette: ["#EED89A", "#a78bfa"], count: 12 },             // layers — editorial
  "11": { palette: ["#EED89A", "#a78bfa"], count: 12 },             // streams — editorial
  "12": { palette: ["#EED89A", "#f6a26a", "#ffe9c0"], count: 24 },  // ambient room — warm
  "13": { palette: ["#f6a26a", "#EED89A", "#ffb3c7"], count: 24 },  // heart wearable — warm rose
  "14": { palette: ["#EED89A", "#a78bfa"], count: 12 },             // ask — editorial
};

function AmbientParticles({ palette, count = 20 }) {
  const motes = useMemo(() => {
    const pal = palette && palette.length ? palette : ["#EED89A"];
    return Array.from({ length: count }, (_, i) => ({
      left: Math.random() * 100,
      top: 15 + Math.random() * 80,            // start low; they drift upward
      size: 1.5 + Math.random() * 2.8,
      color: pal[i % pal.length],
      dx: (Math.random() - 0.5) * 90,          // sideways wander, px
      dy: -(40 + Math.random() * 130),         // upward drift, px
      o: 0.25 + Math.random() * 0.5,
      dur: 9 + Math.random() * 12,
      delay: -Math.random() * 18,              // mid-flight on mount
    }));
  }, [palette, count]);
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden data-testid="pd-ambient-fx">
      {motes.map((m, i) => (
        <span
          key={i}
          className="pd-mote"
          style={{
            left: `${m.left}%`,
            top: `${m.top}%`,
            width: m.size,
            height: m.size,
            background: m.color,
            boxShadow: `0 0 ${m.size * 3.2}px ${m.color}`,
            "--dx": `${m.dx}px`,
            "--dy": `${m.dy}px`,
            "--mo": m.o,
            animationDuration: `${m.dur}s`,
            animationDelay: `${m.delay}s`,
          }}
        />
      ))}
    </div>
  );
}

/* Ocean slide (08) — breathing sun, star-flash glints, undulating reflection trail */
const OCEAN_RIPPLES = [
  { top: "62%", left: 25,   w: 44, d: "0s" },
  { top: "68%", left: 26.5, w: 60, d: "1.1s" },
  { top: "74%", left: 28,   w: 52, d: "2.2s" },
  { top: "81%", left: 30,   w: 74, d: "0.6s" },
  { top: "88%", left: 32.5, w: 88, d: "1.7s" },
  { top: "94%", left: 34.5, w: 64, d: "2.8s" },
];

function OceanShimmer() {
  return (
    <div className="pd-ocean-fx absolute inset-0 pointer-events-none overflow-hidden" aria-hidden data-testid="pd-ocean-fx">
      {/* Breathing sun glow at the horizon */}
      <div className="pd-sun-glow" />
      {/* Star-flash glints around the sun */}
      <span className="pd-sun-glint" style={{ top: "54.2%", left: "calc(23.6% + var(--sx, 0%))", animationDelay: "0s" }} />
      <span className="pd-sun-glint" style={{ top: "57.4%", left: "calc(26.4% + var(--sx, 0%))", animationDelay: "1.5s" }} />
      <span className="pd-sun-glint" style={{ top: "55.8%", left: "calc(24.6% + var(--sx, 0%))", animationDelay: "2.7s" }} />
      {/* Undulating light trail on the water */}
      <div className="pd-water-shimmer" />
      {/* Ripple glints along the reflection path */}
      {OCEAN_RIPPLES.map((r, i) => (
        <span
          key={i}
          className="pd-ripple"
          style={{ top: r.top, left: `calc(${r.left}% + var(--sx, 0%))`, width: r.w, animationDelay: r.d }}
        />
      ))}
    </div>
  );
}

/* Slide 01 — twinkling star field over the cosmic city sky */
function StarTwinkleField() {
  const stars = useMemo(() => Array.from({ length: 26 }, (_, i) => ({
    left: 32 + Math.random() * 63,
    top: 4 + Math.random() * 58,
    size: 1.4 + Math.random() * 2,
    color: ["#ffffff", "#c5b0ff", "#EED89A", "#9db8ff"][i % 4],
    dur: 2.6 + Math.random() * 3.4,
    delay: -Math.random() * 6,
  })), []);
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden data-testid="pd-stars-fx">
      {stars.map((s, i) => (
        <span
          key={i}
          className="pd-star"
          style={{
            left: `${s.left}%`,
            top: `${s.top}%`,
            width: s.size,
            height: s.size,
            background: s.color,
            boxShadow: `0 0 ${s.size * 3}px ${s.color}`,
            animationDuration: `${s.dur}s`,
            animationDelay: `${s.delay}s`,
          }}
        />
      ))}
      {/* Hero cross-flare stars */}
      <span className="pd-sun-glint" style={{ top: "16%", left: "55%", animationDelay: "0.8s" }} />
      <span className="pd-sun-glint" style={{ top: "28%", left: "74%", animationDelay: "2.4s" }} />
      <span className="pd-sun-glint" style={{ top: "10%", left: "89%", animationDelay: "3.6s" }} />
    </div>
  );
}

/* Slide 03 — breathing glow + slow energy ring on the held orb */
function OrbGlowFX() {
  return (
    <div className="pd-orb-fx absolute inset-0 pointer-events-none overflow-hidden" aria-hidden data-testid="pd-orb-fx">
      <div className="pd-orb-glow" />
      <div className="pd-orb-ring" />
      <span className="pd-sun-glint" style={{ top: "44%",   left: "calc(61.2% + var(--ox, 0%))", animationDelay: "1.2s" }} />
      <span className="pd-sun-glint" style={{ top: "50.5%", left: "calc(65% + var(--ox, 0%))",   animationDelay: "3s" }} />
    </div>
  );
}

/* Slide 06 — lights drifting along the mountain path (reuses the mote float engine) */
const PATH_MOTES = [
  { left: 16, top: 83, dx: 46, dy: -10, size: 2.4, dur: 8.5,  delay: 0 },
  { left: 24, top: 86, dx: 52, dy: -6,  size: 2.0, dur: 9.5,  delay: 1.4 },
  { left: 33, top: 88, dx: 58, dy: -8,  size: 2.8, dur: 8,    delay: 2.6 },
  { left: 42, top: 90, dx: 50, dy: -12, size: 2.2, dur: 10,   delay: 0.8 },
  { left: 51, top: 88, dx: 62, dy: -8,  size: 3.0, dur: 9,    delay: 3.4 },
  { left: 60, top: 86, dx: 48, dy: -6,  size: 2.1, dur: 8.8,  delay: 1.9 },
  { left: 68, top: 89, dx: 55, dy: -10, size: 2.6, dur: 9.2,  delay: 4.2 },
];

function PathLightFX() {
  const skyStars = useMemo(() => Array.from({ length: 14 }, (_, i) => ({
    left: 4 + Math.random() * 56,
    top: 5 + Math.random() * 45,
    size: 1.3 + Math.random() * 1.8,
    color: ["#ffffff", "#c5b0ff", "#EED89A"][i % 3],
    dur: 2.8 + Math.random() * 3.2,
    delay: -Math.random() * 6,
  })), []);
  return (
    <>
      {/* Warm aura lifting the seated figure + twinkling mountain sky — always visible */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden>
        <div className="pd-aura-glow" />
        {skyStars.map((s, i) => (
          <span
            key={i}
            className="pd-star"
            style={{
              left: `${s.left}%`,
              top: `${s.top}%`,
              width: s.size,
              height: s.size,
              background: s.color,
              boxShadow: `0 0 ${s.size * 3}px ${s.color}`,
              animationDuration: `${s.dur}s`,
              animationDelay: `${s.delay}s`,
            }}
          />
        ))}
      </div>
      {/* Lights drifting along the glowing trail (landscape windows only) */}
      <div className="pd-path-fx absolute inset-0 pointer-events-none overflow-hidden" aria-hidden data-testid="pd-path-fx">
        {PATH_MOTES.map((m, i) => (
          <span
            key={i}
            className="pd-mote"
            style={{
              left: `${m.left}%`,
              top: `${m.top}%`,
              width: m.size,
              height: m.size,
              background: "#EED89A",
              boxShadow: "0 0 9px #EED89A",
              "--dx": `${m.dx}px`,
              "--dy": `${m.dy}px`,
              "--mo": 0.85,
              animationDuration: `${m.dur}s`,
              animationDelay: `${-m.delay}s`,
            }}
          />
        ))}
      </div>
    </>
  );
}

function SlideBackground({ image, focal, fx, overlay, soft }) {
  // focal="left" → object-position moves image right so text on right stays clear; and vice-versa.
  // With focal="right" (default) we align image to the right and darken the left.
  const isLeftFocal = focal === "left";
  // Editorial gradient fallback for image-less slides (e.g., The Ask — data-focused)
  if (!image) {
    return (
      <>
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(120% 90% at 100% 0%, rgba(238,216,154,0.10) 0%, transparent 55%)," +
              "radial-gradient(90% 80% at 0% 100%, rgba(167,139,250,0.14) 0%, transparent 55%)," +
              "radial-gradient(80% 80% at 50% 50%, rgba(15,30,68,0.6) 0%, #03060f 90%)",
          }}
        />
        {/* Subtle animated grain-ish shimmer via two blurred orbs */}
        <div className="absolute inset-0 pointer-events-none opacity-70">
          <div className="absolute top-[10%] right-[8%] w-[36vw] h-[36vw] rounded-full bg-amber-300/10 blur-[140px]" />
          <div className="absolute bottom-[6%] left-[12%] w-[32vw] h-[32vw] rounded-full bg-violet-500/15 blur-[140px]" />
        </div>
        {/* Editorial hairline top/bottom */}
        <div className="absolute top-16 left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-300/25 to-transparent" />
        <div className="absolute bottom-16 left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-300/20 to-transparent" />
        <AmbientParticles palette={fx?.palette} count={fx?.count ?? 12} />
      </>
    );
  }
  return (
    <>
      <img
        src={image}
        alt=""
        aria-hidden
        className="absolute inset-0 w-full h-full object-cover"
        style={{ objectPosition: isLeftFocal ? "left center" : "right center" }}
        loading="eager"
        decoding="async"
      />
      {/* Dark overlay tuned to the text side */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: isLeftFocal
            ? "linear-gradient(90deg, rgba(3,7,18,0.15) 0%, rgba(3,7,18,0.55) 45%, rgba(3,7,18,0.92) 100%)"
            : "linear-gradient(90deg, rgba(3,7,18,0.92) 0%, rgba(3,7,18,0.55) 45%, rgba(3,7,18,0.15) 100%)",
        }}
      />
      {/* Top/bottom vignette — softened when a slide has a foreground figure to keep visible */}
      <div className={`absolute inset-0 pointer-events-none bg-gradient-to-b from-[#03060f]/40 via-transparent ${soft ? "to-[#03060f]/30" : "to-[#03060f]/60"}`} />
      {overlay === "ocean" && <OceanShimmer />}
      {overlay === "stars" && <StarTwinkleField />}
      {overlay === "orb" && <OrbGlowFX />}
      {overlay === "path" && <PathLightFX />}
      <AmbientParticles palette={fx?.palette} count={fx?.count ?? 22} />
    </>
  );
}

function SlideShell({ slide, children, index }) {
  const alignRight = slide.align === "right";
  const isAsk = slide.variant === "ask";
  const isDashboard = slide.variant === "dashboard";
  const isThesis = slide.variant === "thesis";
  const isLayers = slide.variant === "layers";
  const isStreams = slide.variant === "streams";
  const isScrollable = isLayers || isStreams || slide.scroll === true;
  const justifyClass = (isAsk || isDashboard || isThesis || isLayers || isStreams) ? "justify-center" : (alignRight ? "justify-end" : "justify-start");
  const widthClass = (isDashboard || isLayers || isStreams) ? "max-w-6xl" : (isThesis ? "max-w-4xl" : (isAsk ? "max-w-3xl" : "max-w-2xl"));
  return (
    <section
      className={`pd-slide relative w-full h-full ${isScrollable ? "overflow-y-auto" : "overflow-hidden"}`}
      data-testid={`pitch-slide-${index + 1}`}
      key={`slide-${index}`}
    >
      <SlideBackground image={slide.image} focal={slide.focal} fx={SLIDE_FX[slide.n]} overlay={slide.overlay} soft={slide.softVignette} />
      <div className={`relative z-10 ${isScrollable ? "min-h-full py-24" : "h-full"} w-full flex ${justifyClass} items-center px-6 sm:px-12 lg:px-20`}>
        <div className={`${widthClass} text-left pd-slide-in`}>
          <div className="flex items-baseline gap-3 mb-4">
            <span className="text-[11px] font-heading tracking-[0.28em] uppercase text-amber-300/80">{slide.n}</span>
            <span className="h-px w-8 bg-amber-300/40" />
            <span className="text-[11px] font-heading tracking-[0.28em] uppercase text-zinc-300/85">{slide.eyebrow}</span>
          </div>
          {children}
        </div>
      </div>
    </section>
  );
}

/* Slide variants ------------------------------------------------ */

function CoverSlide({ slide, index }) {
  return (
    <SlideShell slide={slide} index={index}>
      <h1 className="font-heading font-light text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-balance">
        {slide.title}
      </h1>
      <p className="mt-6 font-body text-zinc-300/90 text-base sm:text-lg leading-relaxed max-w-xl">
        {slide.kicker}
      </p>
      <p className="mt-2 font-heading italic text-amber-200/85 text-sm sm:text-base tracking-tight">
        {slide.footer}
      </p>
      <p className="mt-8 text-[10px] uppercase tracking-[0.24em] text-amber-200/80 font-body" data-testid="patent-pending-note">
        U.S. Patent Pending · Confirmed August 2026
      </p>
    </SlideShell>
  );
}

function StandardSlide({ slide, index }) {
  return (
    <SlideShell slide={slide} index={index}>
      <h2 className="font-heading font-light text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-balance">
        {slide.title}
      </h2>
      {slide.body && (
        <p className="mt-5 font-body text-zinc-200/90 text-[15.5px] sm:text-base leading-relaxed">
          {slide.body}
        </p>
      )}

      {slide.bullets && (
        <ul className="mt-7 space-y-4">
          {slide.bullets.map((b, i) => (
            <li key={i} className="flex gap-3">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-amber-300 flex-shrink-0" />
              <div>
                <div className="text-white font-heading text-[15px]">{b.label}</div>
                <div className="text-zinc-300/85 text-[13.5px] leading-relaxed">{b.text}</div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {slide.steps && (
        <ol className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {slide.steps.map((s) => (
            <li key={s.n} className="pd-step flex gap-3 rounded-xl border border-white/12 bg-white/[0.04] backdrop-blur-md px-3.5 py-3">
              <span className="font-heading text-amber-300/85 text-[11px] tracking-[0.2em] uppercase mt-0.5">{s.n}</span>
              <div>
                <div className="text-white font-heading text-[14px]">{s.label}</div>
                <div className="text-zinc-300/80 text-[12.5px] leading-snug">{s.text}</div>
              </div>
            </li>
          ))}
        </ol>
      )}

      {slide.pillars && (
        <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {slide.pillars.map((p, i) => (
            <div key={i} className="pd-step rounded-xl border border-white/12 bg-white/[0.04] backdrop-blur-md px-4 py-3.5">
              <div className="text-[10px] uppercase tracking-[0.24em] text-amber-300/85">{p.tag}</div>
              <div className="text-white font-heading text-[14.5px] mt-0.5">{p.head}</div>
              <div className="text-zinc-300/80 text-[12.5px] leading-snug mt-1">{p.text}</div>
            </div>
          ))}
        </div>
      )}

      {slide.pull && (
        <p className="mt-6 font-heading italic text-amber-200/90 text-[13.5px] sm:text-sm leading-relaxed border-l-2 border-amber-300/40 pl-4">
          {slide.pull}
        </p>
      )}
    </SlideShell>
  );
}

/* Small deck-slide bezier smoothing helper */
function pdSmoothPath(values, W, H, pad = 10) {
  const iw = W - pad * 2, ih = H - pad * 2;
  const pts = values.map((v, i) => [pad + (i / (values.length - 1)) * iw, pad + (1 - v / 100) * ih]);
  if (pts.length < 2) return { d: "", pts: [] };
  let d = `M ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const [x0, y0] = pts[i]; const [x1, y1] = pts[i + 1];
    const cpx = (x0 + x1) / 2;
    d += ` C ${cpx} ${y0}, ${cpx} ${y1}, ${x1} ${y1}`;
  }
  return { d, pts };
}

const PD_DASH_ICON = {
  sparkles: (c) => <path d="M12 3l1.7 4.6L18 9l-4.3 1.4L12 15l-1.7-4.6L6 9l4.3-1.4L12 3z" fill={c} />,
  battery:  (c) => <g><rect x="4" y="8" width="14" height="8" rx="2" stroke={c} strokeWidth="1.6" fill="none"/><rect x="18.5" y="10.5" width="1.5" height="3" fill={c}/><rect x="6" y="10" width="6" height="4" fill={c}/></g>,
  focus:    (c) => <g><circle cx="12" cy="12" r="7" stroke={c} strokeWidth="1.6" fill="none"/><circle cx="12" cy="12" r="2" fill={c}/><line x1="12" y1="2" x2="12" y2="5" stroke={c} strokeWidth="1.6"/><line x1="12" y1="19" x2="12" y2="22" stroke={c} strokeWidth="1.6"/><line x1="2" y1="12" x2="5" y2="12" stroke={c} strokeWidth="1.6"/><line x1="19" y1="12" x2="22" y2="12" stroke={c} strokeWidth="1.6"/></g>,
  moon:     (c) => <path d="M20 14a8 8 0 1 1-9-11 6 6 0 0 0 9 11z" fill="none" stroke={c} strokeWidth="1.6" strokeLinejoin="round"/>,
};

function DashboardSlide({ slide, index }) {
  const t = slide.trend;
  const W = 720, H = 200;
  const vibe   = pdSmoothPath(t.vibe,   W, H);
  const energy = pdSmoothPath(t.energy, W, H);
  const focus  = pdSmoothPath(t.focus,  W, H);
  const rest   = pdSmoothPath(t.rest,   W, H);
  return (
    <SlideShell slide={slide} index={index}>
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-8 items-center">
        {/* LEFT: copy */}
        <div>
          <h2 className="font-heading font-light text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-balance">
            {slide.title}
          </h2>
          <p className="mt-5 font-body text-zinc-200/90 text-[15.5px] sm:text-base leading-relaxed">
            {slide.body}
          </p>
          {slide.items && (
            <div className="mt-5 space-y-2.5">
              {slide.items.map((it) => (
                <div key={it.tag} className="flex gap-3 items-baseline">
                  <span className="shrink-0 w-[78px] text-[10px] uppercase tracking-[0.22em] text-amber-300/85 font-heading">{it.tag}</span>
                  <span className="text-zinc-300/85 text-[12.5px] leading-snug">{it.text}</span>
                </div>
              ))}
            </div>
          )}
          {slide.pull && (
            <p className="mt-6 font-heading italic text-amber-200/90 text-[13.5px] sm:text-sm leading-relaxed border-l-2 border-amber-300/40 pl-4">
              {slide.pull}
            </p>
          )}
        </div>

        {/* RIGHT: mini dashboard */}
        <div className="pd-dash rounded-2xl border border-white/12 bg-gradient-to-b from-[#0e1d42]/85 to-[#0b1738]/80 backdrop-blur-md p-5 shadow-[0_20px_60px_rgba(0,0,0,0.4)]">
          {/* Metrics row */}
          <div className="grid grid-cols-4 gap-2.5 mb-4">
            {slide.metrics.map((m) => {
              const R = 22, C = 2 * Math.PI * R;
              const off = C - (m.value / 100) * C;
              const IconFn = PD_DASH_ICON[m.icon] || PD_DASH_ICON.sparkles;
              return (
                <div key={m.label} className="rounded-xl border border-white/10 bg-white/[0.03] p-2.5 flex items-center gap-2">
                  <div className="relative flex-shrink-0">
                    <svg width="52" height="52" viewBox="0 0 52 52">
                      <circle cx="26" cy="26" r={R} stroke="rgba(255,255,255,0.08)" strokeWidth="4" fill="none" />
                      <circle cx="26" cy="26" r={R} stroke={m.color} strokeWidth="4" fill="none"
                        strokeDasharray={C} strokeDashoffset={off} strokeLinecap="round"
                        transform="rotate(-90 26 26)"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <svg width="18" height="18" viewBox="0 0 24 24">{IconFn(m.color)}</svg>
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[9.5px] uppercase tracking-[0.22em] text-zinc-400 truncate">{m.label}</div>
                    <div className="font-heading text-white text-[18px] leading-none mt-0.5">{m.value}</div>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Trend chart */}
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-400">The trend · Last 6 months</div>
              <div className="flex items-center gap-2.5 text-[9.5px] text-zinc-300/90">
                <span className="inline-flex items-center gap-1"><span className="w-2 h-1 rounded-full bg-[#7de3c5]" />Vibe</span>
                <span className="inline-flex items-center gap-1"><span className="w-2 h-1 rounded-full bg-[#EED89A]" />Energy</span>
                <span className="inline-flex items-center gap-1"><span className="w-2 h-1 rounded-full bg-[#6ea3ff]" />Focus</span>
                <span className="inline-flex items-center gap-1"><span className="w-2 h-1 rounded-full bg-[#c5b0ff]" />Rest</span>
              </div>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="auto" className="block">
              <defs>
                <linearGradient id="pd-vibe-fill" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#7de3c5" stopOpacity="0.28" />
                  <stop offset="100%" stopColor="#7de3c5" stopOpacity="0" />
                </linearGradient>
              </defs>
              {[0, 50, 100].map((g) => {
                const y = 10 + (1 - g / 100) * (H - 30);
                return <line key={g} x1="10" y1={y} x2={W - 10} y2={y} stroke="rgba(255,255,255,0.06)" strokeDasharray="2 4" />;
              })}
              <path d={`${vibe.d} L ${W - 10} ${H - 20} L 10 ${H - 20} Z`} fill="url(#pd-vibe-fill)" />
              <path d={rest.d}   fill="none" stroke="#c5b0ff" strokeWidth="1.8" strokeLinecap="round" opacity="0.85" className="pd-dash-line" />
              <path d={focus.d}  fill="none" stroke="#6ea3ff" strokeWidth="2.0" strokeLinecap="round" opacity="0.9"  className="pd-dash-line" />
              <path d={energy.d} fill="none" stroke="#EED89A" strokeWidth="2.0" strokeLinecap="round" opacity="0.9"  className="pd-dash-line" />
              <path d={vibe.d}   fill="none" stroke="#7de3c5" strokeWidth="2.6" strokeLinecap="round" className="pd-dash-line pd-dash-line-vibe" />
              {[["#c5b0ff", rest.pts], ["#6ea3ff", focus.pts], ["#EED89A", energy.pts], ["#7de3c5", vibe.pts]].map(([c, pts], i) => {
                const [x, y] = pts[pts.length - 1];
                return <circle key={i} cx={x} cy={y} r={i === 3 ? 4.5 : 3.2} fill={c} stroke="#0e1d42" strokeWidth="1.2" />;
              })}
              {t.labels.map((lbl, i) => {
                const bx = 10 + (i / (t.labels.length - 1)) * (W - 20);
                return <text key={lbl} x={bx} y={H - 4} fill="rgba(255,255,255,0.5)" fontSize="9" textAnchor="middle" fontFamily="var(--font-body)">{lbl}</text>;
              })}
            </svg>
          </div>
          {/* Groove library strip — return & share nuances */}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-white/10 pt-3.5">
            <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-400">
              Groove library · <span className="text-teal-300/90">12 ready to revisit</span>
            </span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-400">
              Sharing · <span className="text-amber-200/90">atmosphere only</span>
            </span>
          </div>
        </div>
      </div>
    </SlideShell>
  );
}

function ThesisSlide({ slide, index }) {
  return (
    <SlideShell slide={slide} index={index}>
      <div className="relative text-center pt-4">
        {/* Decorative sextant lines */}
        <div className="mx-auto mb-8 h-px w-24 bg-gradient-to-r from-transparent via-amber-300/60 to-transparent" aria-hidden />
        <blockquote className="font-heading font-light text-white italic text-[22px] sm:text-[26px] lg:text-[30px] leading-[1.35] tracking-tight text-balance">
          <span className="pd-thesis-quote">"{slide.quote}"</span>
        </blockquote>
        <div className="mx-auto mt-10 h-px w-24 bg-gradient-to-r from-transparent via-amber-300/40 to-transparent" aria-hidden />
        <p className="mt-6 text-[12px] uppercase tracking-[0.28em] text-amber-200/85">
          {slide.footer}
        </p>
        <p className="mt-3 text-[11px] font-heading italic text-zinc-400/80 tracking-tight">
          — My Mood Groove working thesis, 2026
        </p>
      </div>
    </SlideShell>
  );
}

function AskSlide({ slide, index }) {
  return (
    <SlideShell slide={slide} index={index}>
      <h2 className="font-heading font-light text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-balance">
        {slide.title}
      </h2>
      <p className="mt-5 font-body text-zinc-200/90 text-[15px] leading-relaxed">
        {slide.body}
      </p>

      {/* Allocation bar */}
      <div className="mt-7">
        <div className="text-[10px] uppercase tracking-[0.24em] text-amber-300/85 mb-2">Use of funds</div>
        <div className="h-3 w-full rounded-full overflow-hidden border border-white/15 flex">
          {slide.allocations.map((a, i) => (
            <div
              key={i}
              className="h-full pd-bar-seg"
              style={{ width: `${a.pct}%`, background: a.color, animationDelay: `${0.15 + i * 0.08}s` }}
              title={`${a.pct}% · ${a.label}`}
            />
          ))}
        </div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
          {slide.allocations.map((a, i) => (
            <div key={i} className="flex items-center gap-2 text-[12.5px]">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: a.color }} />
              <span className="text-white font-heading font-medium">{a.pct}%</span>
              <span className="text-zinc-300/85">{a.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Milestones */}
      <div className="mt-6 rounded-xl border border-amber-300/25 bg-white/[0.03] backdrop-blur-md px-4 py-3.5">
        <div className="text-[10px] uppercase tracking-[0.24em] text-amber-300/85 mb-2">18-month milestones</div>
        <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[13px] text-zinc-100">
          {slide.milestones.map((m, i) => (
            <span key={i} className="inline-flex items-center gap-1.5">
              <span className="w-1 h-1 rounded-full bg-amber-300/80" />
              {m}
            </span>
          ))}
        </div>
      </div>

      <p className="mt-5 font-heading italic text-amber-200/90 text-[13.5px] leading-relaxed border-l-2 border-amber-300/40 pl-4">
        {slide.pull}
      </p>
      <p className="mt-6 text-[10px] uppercase tracking-[0.24em] text-amber-200/80 font-body" data-testid="patent-pending-note">
        U.S. Patent Pending · Confirmed August 2026
      </p>
    </SlideShell>
  );
}

function RevenueLayersSlide({ slide, index }) {
  return (
    <SlideShell slide={slide} index={index}>
      <h2 className="font-heading font-light text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-balance">
        {slide.title}
      </h2>
      <p className="mt-5 font-body text-zinc-200/90 text-[15px] leading-relaxed max-w-3xl">
        {slide.body}
      </p>
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5" data-testid="pd-layers-grid">
        {slide.layers.map((l) => (
          <div key={l.n} className="pd-step rounded-xl border border-white/12 bg-white/[0.04] backdrop-blur-md px-4 py-4">
            <div className="flex items-baseline gap-2.5">
              <span className="text-[10px] font-heading tracking-[0.2em] text-amber-300/70">{l.n}</span>
              <span className="text-white font-heading text-[15px] tracking-tight">{l.payer}</span>
            </div>
            <div className="mt-1 font-heading italic text-amber-200/85 text-[12.5px]">{l.what}</div>
            <div className="mt-1.5 text-zinc-300/80 text-[12.5px] leading-snug">{l.detail}</div>
          </div>
        ))}
      </div>
      <p className="mt-7 font-heading italic text-amber-200/90 text-[13.5px] sm:text-sm leading-relaxed border-l-2 border-amber-300/40 pl-4 max-w-3xl">
        {slide.pull}
      </p>
    </SlideShell>
  );
}

function RevenueStreamsSlide({ slide, index }) {
  return (
    <SlideShell slide={slide} index={index}>
      <h2 className="font-heading font-light text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-balance">
        {slide.title}
      </h2>
      <p className="mt-4 font-body text-zinc-200/90 text-[15px] leading-relaxed max-w-3xl">
        {slide.body}
      </p>
      <div className="mt-7 grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="pd-streams-phases">
        {slide.phases.map((p) => (
          <div key={p.phase} className="relative rounded-2xl border border-white/12 bg-white/[0.03] backdrop-blur-md p-4 overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: `linear-gradient(90deg, ${p.color}66, transparent)` }} />
            <div className="flex items-baseline gap-2">
              <span className="text-[10px] uppercase tracking-[0.24em] font-heading" style={{ color: p.color }}>{p.phase}</span>
              <span className="text-white font-heading text-[15px] tracking-tight">{p.name}</span>
            </div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-400 mt-0.5">{p.focus}</div>
            <div className="mt-3.5 space-y-3.5">
              {p.items.map((it, j) => (
                <div key={j} className="border-l pl-3" style={{ borderColor: `${p.color}44` }}>
                  <div className="text-white font-heading text-[13.5px] leading-tight">{it.head}</div>
                  <div className="text-zinc-300/80 text-[12px] leading-snug mt-0.5">{it.text}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <p className="mt-6 font-heading italic text-amber-200/90 text-[13px] leading-relaxed border-l-2 border-amber-300/40 pl-4 max-w-4xl">
        {slide.pull}
      </p>
    </SlideShell>
  );
}

/* ────────────────────────────────────────────────────────────────
   Main deck page
   ──────────────────────────────────────────────────────────────── */

export default function PitchDeck({ publicAccess = false }) {
  const [idx, setIdx] = useState(0);
  const navigate = useNavigate();
  const total = SLIDES.length;
  const authed = publicAccess || (typeof window !== "undefined" && sessionStorage.getItem("mmg_investor_access") === "granted");
  // Public viewers exit to the landing page, not the password-gated portal
  const exitTo = publicAccess ? "/" : "/investor";

  // Log public deck views (once per session) so VC traffic shows up in admin stats
  useEffect(() => {
    if (!publicAccess) return;
    if (sessionStorage.getItem("mmg_deck_view_logged") === "1") return;
    sessionStorage.setItem("mmg_deck_view_logged", "1");
    axios.post(`${API}/track/deck-view`).catch(() => {});
  }, [publicAccess]);

  // Ambient audio — starts muted, user toggles on
  const audioRef = useRef(null);
  const fadeIdRef = useRef(null);
  const [audioOn, setAudioOn] = useState(() => {
    if (typeof window === "undefined") return false;
    return sessionStorage.getItem("mmg_deck_audio") === "on";
  });
  // Ambient volume (0–0.6), persisted per session
  const [volume, setVolume] = useState(() => {
    if (typeof window === "undefined") return 0.28;
    const v = parseFloat(sessionStorage.getItem("mmg_deck_volume"));
    return Number.isFinite(v) ? Math.min(0.6, Math.max(0, v)) : 0.28;
  });

  const fadeTo = useCallback((target, durationMs = 900) => {
    const el = audioRef.current;
    if (!el) return;
    if (fadeIdRef.current) { clearInterval(fadeIdRef.current); fadeIdRef.current = null; }
    const start = el.volume;
    const startAt = performance.now();
    fadeIdRef.current = setInterval(() => {
      const t = Math.min(1, (performance.now() - startAt) / durationMs);
      el.volume = start + (target - start) * t;
      if (t >= 1) { clearInterval(fadeIdRef.current); fadeIdRef.current = null; }
    }, 30);
  }, []);

  // Autoplay the ambient track on deck entry. Same-document navigation from a click
  // usually carries user activation; on a fresh browser load the browser may block it,
  // in which case the speaker toggle remains as the one-tap fallback.
  useEffect(() => {
    if (!authed) return;
    const el = audioRef.current;
    if (!el) return;
    el.volume = 0;
    const p = el.play();
    if (p && p.then) {
      p.then(() => {
        fadeTo(volume, 1600);
        setAudioOn(true);
        sessionStorage.setItem("mmg_deck_audio", "on");
      }).catch(() => {
        setAudioOn(false);
        sessionStorage.setItem("mmg_deck_audio", "off");
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const toggleAudio = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    if (audioOn) {
      // Turning OFF — fade out then pause
      fadeTo(0, 500);
      setTimeout(() => { try { el.pause(); } catch (_) {} }, 550);
      setAudioOn(false);
      sessionStorage.setItem("mmg_deck_audio", "off");
    } else {
      // Turning ON — start at 0, play, fade to the slider volume
      el.volume = 0;
      const playPromise = el.play();
      if (playPromise && playPromise.catch) playPromise.catch(() => {});
      fadeTo(volume, 1200);
      setAudioOn(true);
      sessionStorage.setItem("mmg_deck_audio", "on");
    }
  }, [audioOn, fadeTo, volume]);

  // Volume slider — live adjust; sliding up from 0 restarts playback
  const changeVolume = useCallback((v) => {
    setVolume(v);
    sessionStorage.setItem("mmg_deck_volume", String(v));
    const el = audioRef.current;
    if (!el) return;
    if (v <= 0) {
      if (audioOn) {
        fadeTo(0, 250);
        setTimeout(() => { try { el.pause(); } catch (_) {} }, 300);
        setAudioOn(false);
        sessionStorage.setItem("mmg_deck_audio", "off");
      }
    } else {
      if (fadeIdRef.current) { clearInterval(fadeIdRef.current); fadeIdRef.current = null; }
      el.volume = v;
      if (!audioOn) {
        const p = el.play();
        if (p && p.catch) p.catch(() => {});
        setAudioOn(true);
        sessionStorage.setItem("mmg_deck_audio", "on");
      }
    }
  }, [audioOn, fadeTo]);

  // Cleanup on unmount — always stop the audio
  useEffect(() => {
    return () => {
      if (fadeIdRef.current) clearInterval(fadeIdRef.current);
      const el = audioRef.current;
      if (el) { try { el.pause(); el.currentTime = 0; } catch (_) {} }
    };
  }, []);

  const goTo = useCallback((n) => setIdx(() => Math.max(0, Math.min(total - 1, n))), [total]);
  const next = useCallback(() => setIdx((v) => Math.min(total - 1, v + 1)), [total]);
  const prev = useCallback(() => setIdx((v) => Math.max(0, v - 1)), []);

  useEffect(() => {
    if (!authed) return;
    const h = (e) => {
      if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") { e.preventDefault(); next(); }
      else if (e.key === "ArrowLeft" || e.key === "PageUp") { e.preventDefault(); prev(); }
      else if (e.key === "Escape") { navigate(exitTo); }
      else if (e.key === "Home") { setIdx(0); }
      else if (e.key === "End")  { setIdx(total - 1); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [authed, next, prev, navigate, total, exitTo]);

  // Preload adjacent images for smooth transitions
  useEffect(() => {
    if (!authed) return;
    [idx - 1, idx + 1, idx + 2].forEach((i) => {
      if (SLIDES[i] && SLIDES[i].image) { const im = new Image(); im.src = SLIDES[i].image; }
    });
  }, [idx, authed]);

  const slide = SLIDES[idx];
  const SlideComp = useMemo(() => {
    if (slide.variant === "cover") return CoverSlide;
    if (slide.variant === "ask") return AskSlide;
    if (slide.variant === "dashboard") return DashboardSlide;
    if (slide.variant === "thesis") return ThesisSlide;
    if (slide.variant === "layers") return RevenueLayersSlide;
    if (slide.variant === "streams") return RevenueStreamsSlide;
    return StandardSlide;
  }, [slide.variant]);

  if (!authed) {
    return <Navigate to="/investor-access" replace />;
  }

  return (
    <div className="pd-root fixed inset-0 bg-[#03060f] text-white overflow-hidden" data-testid="pitch-deck-root">
      {/* Ambient audio — hidden, controlled via toggle button. Muted+preload for browser autoplay policies. */}
      <audio
        ref={audioRef}
        src="/pitch/Mystic_02.mp3"
        loop
        preload="auto"
        data-testid="pitch-audio"
      />
      {/* Slide stage */}
      <div className="absolute inset-0">
        <SlideComp slide={slide} index={idx} key={idx} />
      </div>

      {/* Top bar: brand + counter + exit */}
      <div className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between px-5 sm:px-8 py-4 pointer-events-none">
        <div className="flex items-center gap-2 pointer-events-auto">
          <Logo size="sm" />
          <span className="text-white/90 font-heading text-[13px] tracking-tight hidden sm:inline">My Mood Groove</span>
          <span className="hidden sm:inline text-white/30 text-[11px] mx-1">·</span>
          <span className="hidden sm:inline text-[10.5px] uppercase tracking-[0.28em] text-amber-300/80">Investor deck</span>
        </div>
        <div className="flex items-center gap-4 pointer-events-auto">
          <span className="text-[11.5px] font-heading tracking-[0.2em] text-zinc-300/80" data-testid="pitch-counter">
            {String(idx + 1).padStart(2, "0")} <span className="text-zinc-500">/ {String(total).padStart(2, "0")}</span>
          </span>
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={toggleAudio}
              className={`inline-flex items-center justify-center w-8 h-8 rounded-full border transition ${audioOn ? "border-amber-300/60 bg-amber-300/10 text-amber-200" : "border-white/15 bg-white/[0.05] hover:bg-white/[0.1] text-white/80"}`}
              data-testid="pitch-audio-toggle"
              title={audioOn ? "Mute ambient sound" : "Enable ambient sound"}
              aria-pressed={audioOn}
              aria-label={audioOn ? "Mute ambient sound" : "Enable ambient sound"}
            >
              {audioOn ? <Volume2 size={14} /> : <VolumeX size={14} />}
            </button>
            <input
              type="range"
              min="0"
              max="60"
              value={Math.round(volume * 100)}
              onChange={(e) => changeVolume(Number(e.target.value) / 100)}
              className="pd-vol w-14 sm:w-20"
              data-testid="pitch-volume-slider"
              aria-label="Ambient volume"
              title="Ambient volume"
            />
          </div>
          <button
            type="button"
            onClick={() => navigate(exitTo)}
            className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.05] hover:bg-white/[0.1] px-3 py-1.5 text-[11.5px] text-white/85 hover:text-white transition"
            data-testid="pitch-exit"
            title="Exit deck (Esc)"
          >
            <X size={13} /> Close
          </button>
        </div>
      </div>

      {/* Prev / Next click zones (desktop only, so the slide content remains selectable) */}
      <button
        type="button"
        onClick={prev}
        disabled={idx === 0}
        aria-label="Previous slide"
        className={`absolute left-0 top-1/2 -translate-y-1/2 z-20 group hidden md:flex items-center justify-center w-14 h-24 rounded-r-xl ${idx === 0 ? "opacity-25 cursor-not-allowed" : "opacity-70 hover:opacity-100"} transition`}
        data-testid="pitch-prev"
      >
        <span className="rounded-full border border-white/20 bg-black/40 backdrop-blur px-2 py-2 group-hover:bg-black/60 transition">
          <ChevronLeft size={20} className="text-white" />
        </span>
      </button>
      <button
        type="button"
        onClick={next}
        disabled={idx === total - 1}
        aria-label="Next slide"
        className={`absolute right-0 top-1/2 -translate-y-1/2 z-20 group hidden md:flex items-center justify-center w-14 h-24 rounded-l-xl ${idx === total - 1 ? "opacity-25 cursor-not-allowed" : "opacity-70 hover:opacity-100"} transition`}
        data-testid="pitch-next"
      >
        <span className="rounded-full border border-white/20 bg-black/40 backdrop-blur px-2 py-2 group-hover:bg-black/60 transition">
          <ChevronRight size={20} className="text-white" />
        </span>
      </button>

      {/* Bottom nav bar */}
      <div className="absolute bottom-0 left-0 right-0 z-30 flex items-center justify-between px-5 sm:px-8 py-4 gap-4">
        <button
          type="button"
          onClick={prev}
          disabled={idx === 0}
          className={`inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.04] hover:bg-white/[0.09] px-3.5 py-1.5 text-[12px] text-white/85 transition ${idx === 0 ? "opacity-40 cursor-not-allowed" : ""}`}
          data-testid="pitch-prev-bottom"
        >
          <ArrowLeft size={13} /> Prev
        </button>

        {/* Progress dots */}
        <div className="flex-1 flex items-center justify-center gap-1.5">
          {SLIDES.map((_, i) => {
            const active = i === idx;
            return (
              <button
                key={i}
                type="button"
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${active ? "w-8 bg-amber-300" : "w-1.5 bg-white/25 hover:bg-white/50"}`}
                data-testid={`pitch-dot-${i + 1}`}
              />
            );
          })}
        </div>

        <button
          type="button"
          onClick={next}
          disabled={idx === total - 1}
          className={`inline-flex items-center gap-1.5 rounded-full bg-gradient-to-b from-[#F4EAC8] to-[#E6D19E] text-[#0F1E44] font-heading font-medium px-3.5 py-1.5 text-[12px] shadow-[0_4px_14px_rgba(238,216,154,0.25)] transition ${idx === total - 1 ? "opacity-40 cursor-not-allowed" : "hover:brightness-105"}`}
          data-testid="pitch-next-bottom"
        >
          {idx === total - 1 ? "End" : "Next"} <ArrowRight size={13} />
        </button>
      </div>

      {/* Micro helper: keyboard hint (auto-hides after first advance) */}
      {idx === 0 && (
        <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-30 text-[10.5px] uppercase tracking-[0.24em] text-white/50 hidden sm:block pointer-events-none">
          Use ← → to navigate · Esc to close
        </div>
      )}
    </div>
  );
}
